/**
 * @fileoverview Key Storage Service - Secure client-side storage of encryption keys using IndexedDB
 *
 * This service provides a secure, browser-based storage mechanism for user encryption keys.
 * Keys are stored in IndexedDB, which provides:
 * - Persistence across browser sessions
 * - Isolation per origin (domain-specific storage)
 * - Asynchronous API for non-blocking operations
 * - Larger storage capacity than localStorage
 *
 * **Storage Architecture:**
 * - Database: 'XanecTechEncryptionKeys' (single database per origin)
 * - Object Store: 'keys' (indexed by userId)
 * - Private keys stored in encrypted form (encrypted by user password)
 * - Public keys stored in PEM format for easy import/export
 *
 * **Security Considerations:**
 * - Private keys are NEVER stored in plaintext
 * - IndexedDB is origin-isolated (same-origin policy)
 * - Keys are not accessible across different domains or subdomains
 * - Browser-level encryption may be applied by some browsers
 * - Keys should be cleared on logout to prevent unauthorized access
 *
 * **Usage Pattern:**
 * 1. Generate key pair → KeyGenerationService
 * 2. Encrypt private key with password → KeyGenerationService.encryptPrivateKey()
 * 3. Store encrypted private key and public key → storeKeys()
 * 4. Retrieve when needed → getStoredKeys()
 * 5. Decrypt private key with password → KeyGenerationService.decryptPrivateKey()
 * 6. Clear on logout → deleteKeys() or clearAllKeys()
 *
 * @module services/KeyStorageService
 * @requires indexedDB
 */

import { createLogger } from '@xanectech/lib';

const logger = createLogger('KeyStorageService');

/**
 * IndexedDB database name for encryption key storage
 * @constant {string}
 */
const DB_NAME = 'XanecTechEncryptionKeys';

/**
 * IndexedDB database version
 * @constant {number}
 */
const DB_VERSION = 1;

/**
 * IndexedDB object store name for keys
 * @constant {string}
 */
const STORE_NAME = 'keys';

/**
 * Structure for stored encryption keys
 *
 * @interface StoredKey
 * @property {string} userId - Unique identifier for the user (primary key)
 * @property {string} encryptedPrivateKey - AES-GCM encrypted private key (encrypted with user password)
 * @property {string} publicKeyPem - RSA public key in PEM format (SPKI encoding)
 * @property {Date} createdAt - Timestamp when keys were first stored
 * @property {Date} lastUsed - Timestamp of last key retrieval or use
 */
export interface StoredKey {
  userId: string;
  encryptedPrivateKey: string;
  publicKeyPem: string;
  createdAt: Date;
  lastUsed: Date;
}

/**
 * Initialize IndexedDB database for key storage
 *
 * Creates or opens the encryption keys database and ensures the object store
 * exists with proper indexes. This function handles database versioning and
 * schema upgrades automatically.
 *
 * @returns {Promise<IDBDatabase>} Open database connection
 * @throws {Error} If IndexedDB is not supported or database creation fails
 *
 * @private
 *
 * @example
 * const db = await initDB();
 * const transaction = db.transaction(['keys'], 'readonly');
 */
async function initDB(): Promise<IDBDatabase> {

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      logger.error('Failed to open IndexedDB');
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;


      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
        store.createIndex('userId', 'userId', { unique: true });
      }
    };
  });
}

/**
 * Store encrypted private key and public key for a user
 *
 * Stores the user's encryption keys in IndexedDB. If keys already exist for
 * this user, they will be overwritten (updated). The private key MUST be
 * encrypted before calling this function.
 *
 * @param {string} userId - Unique identifier for the user
 * @param {string} encryptedPrivateKey - Password-encrypted private key (base64 encoded)
 * @param {string} publicKeyPem - Public key in PEM format (SPKI)
 * @returns {Promise<void>} Resolves when keys are successfully stored
 * @throws {Error} If database operation fails or IndexedDB is unavailable
 *
 * @example
 * // After generating and encrypting keys
 * const keyPair = await generateKeyPair();
 * const exported = await exportKeyPair(keyPair);
 * const encrypted = await encryptPrivateKey(exported.privateKeyPem, password);
 * await storeKeys(userId, encrypted, exported.publicKeyPem);
 */
export async function storeKeys(
  userId: string,
  encryptedPrivateKey: string,
  publicKeyPem: string,
): Promise<void> {

  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const keyData: StoredKey = {
      userId,
      encryptedPrivateKey,
      publicKeyPem,
      createdAt: new Date(),
      lastUsed: new Date(),
    };

    const request = store.put(keyData);

    request.onsuccess = () => {
      db.close();
      resolve();
    };

    request.onerror = () => {
      db.close();
      logger.error('Failed to store encryption keys');
      reject(new Error('Failed to store encryption keys'));
    };
  });
}

/**
 * Retrieve stored encryption keys for a user
 *
 * Fetches the user's encryption keys from IndexedDB. Returns null if no keys
 * exist for the specified user. The private key will still be in encrypted
 * form and must be decrypted before use.
 *
 * @param {string} userId - Unique identifier for the user
 * @returns {Promise<StoredKey | null>} Stored key data or null if not found
 * @throws {Error} If database operation fails
 *
 * @example
 * const keys = await getStoredKeys(userId);
 * if (keys) {
 *   const privateKeyPem = await decryptPrivateKey(
 *     keys.encryptedPrivateKey,
 *     password
 *   );
 *   const keyPair = await importKeyPair({
 *     publicKeyPem: keys.publicKeyPem,
 *     privateKeyPem
 *   });
 * }
 */
export async function getStoredKeys(userId: string): Promise<StoredKey | null> {

  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(userId);

    request.onsuccess = () => {
      db.close();
      const result = (request.result as StoredKey | undefined) || null;
      resolve(result);
    };

    request.onerror = () => {
      db.close();
      logger.error('Failed to retrieve encryption keys');
      reject(new Error('Failed to retrieve encryption keys'));
    };
  });
}

/**
 * Update the last used timestamp for stored keys
 *
 * Records the current time as the last time the keys were accessed.
 * This can be used for key rotation policies or to track inactive keys.
 *
 * @param {string} userId - Unique identifier for the user
 * @returns {Promise<void>} Resolves when timestamp is updated
 * @throws {Error} If database operation fails
 *
 * @example
 * // After successfully using keys for decryption
 * await updateLastUsed(userId);
 */
export async function updateLastUsed(userId: string): Promise<void> {

  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(userId);

    getRequest.onsuccess = () => {
      const keyData = getRequest.result as StoredKey | undefined;
      if (keyData) {
        keyData.lastUsed = new Date();
        const updateRequest = store.put(keyData);

        updateRequest.onsuccess = () => {
          db.close();
          resolve();
        };

        updateRequest.onerror = () => {
          db.close();
          logger.error('Failed to update last used timestamp');
          reject(new Error('Failed to update last used timestamp'));
        };
      } else {
        db.close();
        resolve();
      }
    };

    getRequest.onerror = () => {
      db.close();
      logger.error('Failed to retrieve keys for update');
      reject(new Error('Failed to retrieve keys for update'));
    };
  });
}

/**
 * Delete stored encryption keys for a user
 *
 * Permanently removes the user's encryption keys from IndexedDB.
 * This should be called on logout or when keys need to be regenerated.
 *
 * **Warning:** After deletion, encrypted data cannot be decrypted unless
 * keys are restored from backup or regenerated (data loss risk).
 *
 * @param {string} userId - Unique identifier for the user
 * @returns {Promise<void>} Resolves when keys are deleted
 * @throws {Error} If database operation fails
 *
 * @example
 * // On user logout
 * await deleteKeys(userId);
 * // User will need to re-authenticate to generate/restore keys
 */
export async function deleteKeys(userId: string): Promise<void> {

  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(userId);

    request.onsuccess = () => {
      db.close();
      resolve();
    };

    request.onerror = () => {
      db.close();
      logger.error('Failed to delete encryption keys');
      reject(new Error('Failed to delete encryption keys'));
    };
  });
}

/**
 * Check if encryption keys exist for a user
 *
 * Quick check to determine if keys are stored without retrieving them.
 * Useful for conditional UI flows (setup wizard vs. normal operation).
 *
 * @param {string} userId - Unique identifier for the user
 * @returns {Promise<boolean>} True if keys exist, false otherwise
 * @throws {Error} If database operation fails
 *
 * @example
 * if (await hasKeys(userId)) {
 *   // Show unlock screen
 * } else {
 *   // Show key generation wizard
 * }
 */
export async function hasKeys(userId: string): Promise<boolean> {

  const keys = await getStoredKeys(userId);
  return keys !== null;
}

/**
 * Clear all stored encryption keys from IndexedDB
 *
 * **WARNING:** This is a destructive operation that removes ALL users' keys.
 * Use with extreme caution, typically only for:
 * - Development/testing cleanup
 * - Admin-initiated full reset
 * - Security incident response
 *
 * @returns {Promise<void>} Resolves when all keys are cleared
 * @throws {Error} If database operation fails
 *
 * @example
 * // Development cleanup
 * if (import.meta.env.DEV) {
 *   await clearAllKeys();
 * }
 *
 * @see deleteKeys for single-user key deletion
 */
export async function clearAllKeys(): Promise<void> {

  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => {
      db.close();
      resolve();
    };

    request.onerror = () => {
      db.close();
      logger.error('Failed to clear all keys');
      reject(new Error('Failed to clear all keys'));
    };
  });
}
